import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  PromptStarterV2 as PromptStarter,
  PromptStarterList,
} from "@fluentui-copilot/react-prompt-starter";
import { CopilotProvider } from "@fluentui-copilot/react-provider";
import { CopilotTheme } from "@fluentui-copilot/react-copilot-theme";
import {
  FeedbackButtons,
  Suggestion,
  SuggestionList,
} from "@fluentui-copilot/react-copilot";
import {
  ChainOfThought,
  ChainOfThoughtItem,
} from "@fluentui-copilot/react-chain-of-thought";
import { MorseCode } from "@fluentui-copilot/react-morse-code";
import {
  CopilotChat,
  CopilotMessage,
  UserMessage,
} from "@fluentui-copilot/react-copilot-chat";
import {
  Avatar,
  ToggleButton,
  Image,
  makeStyles,
  Title1,
  makeStaticStyles,
} from '@fluentui/react-components';
import { Button } from '@fluentui/react-button';

import { tokens as copilotTokens } from '@fluentui-copilot/tokens';
import { colorTokens } from '../../../../tokens';
import premiumStarIcon from '../../../../assets/shared/Images/premium-star.svg';
import { useLLMContext } from '../layout';
import { azureOpenAIService } from '../../../../llm/services/AzureOpenAIService';
import {
  bundleIcon,
  Add20Regular,
  Add20Filled,
  ArrowRight16Regular,
  ArrowRight16Filled,
  Briefcase20Regular,
  Briefcase20Filled,
  Calendar20Regular,
  Chat20Regular,
  Compose20Regular,
  Compose20Filled,
  DeviceEq20Regular,
  DeviceEq20Filled,
  Dismiss20Regular,
  Dismiss20Filled,
  Globe20Regular,
  Globe20Filled,
  Mic20Regular,
  Mic20Filled,
  MoreHorizontal20Regular,
  ShieldTaskRegular,
  ShieldTaskFilled,
  TextAlignLeftRegular,
  TextAlignLeftFilled,
  ThumbDislikeRegular,
  ThumbDislikeFilled,
  ThumbLikeRegular,
  ThumbLikeFilled,
} from '@fluentui/react-icons';

// Bundle icons for hover effects (Regular on rest, Filled on hover)
const Add20 = bundleIcon(Add20Filled, Add20Regular);
const ArrowRight16 = bundleIcon(ArrowRight16Filled, ArrowRight16Regular);
const Compose20 = bundleIcon(Compose20Filled, Compose20Regular);
const DeviceEq20 = bundleIcon(DeviceEq20Filled, DeviceEq20Regular);
const Dismiss20 = bundleIcon(Dismiss20Filled, Dismiss20Regular);
const Mic20 = bundleIcon(Mic20Filled, Mic20Regular);
const ShieldTask = bundleIcon(ShieldTaskFilled, ShieldTaskRegular);
const TextAlignLeft = bundleIcon(TextAlignLeftFilled, TextAlignLeftRegular);
const ThumbDislike = bundleIcon(ThumbDislikeFilled, ThumbDislikeRegular);
const ThumbLike = bundleIcon(ThumbLikeFilled, ThumbLikeRegular);

type AppType = 'word' | 'excel' | 'powerpoint' | 'onenote' | 'forms';

// Initial message types
export interface InitialMessage {
  type: 'user' | 'assistant';
  content: string;
}

interface CopilotPaneProps {
  isOpen: boolean;
  onClose: () => void;
  appType?: AppType;
  initialMessage?: InitialMessage;
  documentContent?: string; // Document content for context-aware responses
}

export type { CopilotPaneProps };

// Helper function to get brand colors based on app type
const getBrandColors = (appType?: AppType) => {
  switch (appType) {
    case 'onenote':
      return {
        primary: colorTokens.oneNoteBrand,
        hover: colorTokens.oneNoteBrandAlt,
        foreground: colorTokens.oneNoteBrand,
      };
    case 'excel':
      return {
        primary: colorTokens.excelBrand,
        hover: colorTokens.excelBrandAlt,
        foreground: colorTokens.excelBrand,
      };
    case 'word':
      return {
        primary: colorTokens.wordBrand,
        hover: colorTokens.wordBrandAlt,
        foreground: colorTokens.wordBrand,
      };
    case 'powerpoint':
      return {
        primary: colorTokens.powerPointBrand,
        hover: colorTokens.powerPointBrandAlt,
        foreground: colorTokens.powerPointBrand,
      };
    default:
      return {
        primary: copilotTokens.colorBrandBackground,
        hover: copilotTokens.colorBrandBackgroundHover,
        foreground: copilotTokens.colorBrandForeground1,
      };
  }
};

const PromptStarterActions = () => {
  const styles = useStyles();
  
  return (
    <>
      <Button
        aria-label="like"
        size="small"
        appearance="transparent"
        icon={<ThumbLike />}
        className={styles.brandHoverButton}
      />
      <Button
        aria-label="dislike"
        size="small"
        appearance="transparent"
        icon={<ThumbDislike />}
        className={styles.brandHoverButton}
      />
    </>
  );
};

const useStaticStyles = makeStaticStyles({
  html: {
    scrollbarGutter: "stable",
  },
  ".sb-show-main, .fui-FluentProvider": {
    padding: "0px !important",
    margin: "0px auto",
  },
  "@keyframes morseCodePulse": {
    "0%, 100%": { opacity: "0.3", transform: "scale(1)" },
    "50%": { opacity: "1", transform: "scale(1.2)" },
  },
  "@keyframes morseCodeSlide": {
    "0%": { transform: "translateX(-100%)" },
    "100%": { transform: "translateX(100%)" },
  },
});

const useStyles = makeStyles({
  container: {
    width: '380px', // Maintain the 380px width as requested
    minWidth: '380px', // Ensure it never shrinks below 380px
    maxWidth: '380px', // Ensure it never grows beyond 380px
    height: '100%',
    backgroundColor: copilotTokens.colorNeutralBackground2,
    borderLeft: `1px solid ${copilotTokens.colorNeutralStroke1}`,
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
  },
  
  header: {
    padding: copilotTokens.spacingVerticalM,
    backgroundColor: 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
    height: '56px', // Updated to 56px
  },

  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: copilotTokens.spacingHorizontalXXS,
  },

  headerCenter: {
    display: 'flex',
    alignItems: 'center',
    gap: copilotTokens.spacingHorizontalNone,
  },

  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: copilotTokens.spacingHorizontalXS,
  },

  iconButton: {
    minWidth: '32px',
    minHeight: '32px',
  },

  brandHoverButton: {
    minWidth: '32px',
    minHeight: '32px',
    backgroundColor: copilotTokens.colorTransparentBackground,
    color: copilotTokens.colorNeutralForeground2,
    '&:hover': {
      color: 'var(--brand-color)',
    },
  },

  greenIconButton: {
    minWidth: '32px',
    minHeight: '32px',
    backgroundColor: copilotTokens.colorTransparentBackground,
    color: '#107C10',
    '&:hover': {
      color: '#107C10',
    },
  },

  toggleButtonLeft: {
    minWidth: '32px',
    minHeight: '32px',
    padding: copilotTokens.spacingHorizontalSNudge,
  },

  toggleButtonRight: {
    minWidth: '32px',
    minHeight: '32px',
    padding: copilotTokens.spacingHorizontalSNudge,
  },

  selectedToggleButton: {
    minWidth: '32px',
    minHeight: '32px',
    backgroundColor: copilotTokens.colorNeutralBackground1,
    '&:hover': {
      backgroundColor: copilotTokens.colorNeutralBackground1,
    },
  },

  editButton: {
    backgroundColor: copilotTokens.colorBrandBackground,
    color: copilotTokens.colorNeutralForegroundOnBrand,
    '&:hover': {
      backgroundColor: copilotTokens.colorBrandBackgroundHover,
    },
  },
  
  chatContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: `0 ${copilotTokens.spacingHorizontalL}`,
    paddingTop: copilotTokens.spacingVerticalXL,
    paddingBottom: copilotTokens.spacingVerticalL,
    width: '100%',
    boxSizing: 'border-box',
    overflow: 'hidden',
    minHeight: 0, // Important for flex child to respect container height
  },
  
  provider: {
    backgroundColor: 'transparent', // Let parent background show through
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: copilotTokens.spacingVerticalXXL,
    width: '100%',
    boxSizing: 'border-box',
    flexShrink: 0, // Don't shrink, let spacers handle sizing
  },
  
  visuallyHidden: {
    display: 'none !important',
  },
  
  highlightText: {
    color: copilotTokens.colorBrandForeground1,
    fontWeight: copilotTokens.fontWeightSemibold,
  },

  headerSection: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    width: '100%',
    boxSizing: 'border-box',
    minHeight: "fit-content", // Ensure it never shrinks below content size
  },

  premiumBadge: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    padding: '12px 16px',
    paddingBottom: '24px',
    width: '100%',
    boxSizing: 'border-box',
  },

  premiumIcon: {
    width: '20px',
    height: '20px',
    flexShrink: 0,
  },

  premiumText: {
    fontSize: '12px',
    lineHeight: '16px',
    color: copilotTokens.colorNeutralForeground1,
    fontFamily: 'Segoe UI, system-ui, sans-serif',
  },
  
  // Scrollable content area for prompt starters
  scrollableContent: {
    width: '100%',
    height: '100%',
    overflowY: 'auto',
    overflowX: 'hidden',
    boxSizing: 'border-box',
    minHeight: 0, // Important for flex/grid children to shrink
    maxHeight: '100%', // Ensure it doesn't exceed container height
  },

  // Fix overflow for prompt starters and content
  promptStarterContainer: {
    width: '100%',
    boxSizing: 'border-box',
  },
  
  promptStarterList: {
    width: '100%',
    '& > *': {
      width: '100%',
      maxWidth: '100%',
      boxSizing: 'border-box',
    },
  },

  sendButton: {
    backgroundColor: `${copilotTokens.colorBrandBackground} !important`,
    '&:hover': {
      backgroundColor: `${copilotTokens.colorBrandBackgroundHover} !important`,
    },
  },

  feedbackButtons: {
    '& button': {
      color: copilotTokens.colorNeutralForeground2,
      '&:hover': {
        color: 'var(--brand-color)',
      },
    },
  },

  // Chat view styles
  chatView: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    backgroundColor: copilotTokens.colorNeutralBackground2,
    position: "relative",
    overflow: "hidden",
  },

  chatMessages: {
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden",
    padding: "8px 16px", // Reduced from spacingHorizontalL to minimize white space
    paddingBottom: "24px", // Adjusted for suggestions + input + padding
    display: "flex",
    flexDirection: "column",
    gap: "8px", // Reduced gap between messages
    minHeight: 0, // Important for proper flex behavior
    maxHeight: "100%", // Prevent overflow
  },

  chatInputContainer: {
    position: "sticky",
    bottom: 0,
    left: 0,
    right: 0,
    paddingTop: "12px",
    paddingBottom: "12px",
    paddingLeft: "0px",
    paddingRight: "0px",
    backgroundColor: copilotTokens.colorNeutralBackground2,
    boxSizing: "border-box",
    zIndex: 10,
    flexShrink: 0,
  },

  userMessageBubble: {
    alignSelf: "flex-end",
    backgroundColor: copilotTokens.colorBrandBackground,
    color: copilotTokens.colorNeutralForegroundOnBrand,
    padding: "12px 16px",
    borderRadius: "18px 18px 4px 18px",
    maxWidth: "80%",
    fontSize: "14px",
    lineHeight: "20px",
  },

  copilotMessageBubble: {
    alignSelf: "flex-start",
    backgroundColor: copilotTokens.colorNeutralBackground1,
    color: copilotTokens.colorNeutralForeground1,
    padding: "12px 16px",
    borderRadius: "18px 18px 18px 4px",
    width: "323px", // Fixed width to match Chain of Thought output state
    maxWidth: "323px",
    minWidth: "323px",
    fontSize: "14px",
    lineHeight: "20px",
    border: `1px solid ${copilotTokens.colorNeutralStroke2}`,
  },

  chat: {
    flex: 1,
    overflowY: "auto",
    padding: copilotTokens.spacingVerticalM,
    marginBottom: "0px",
    minHeight: 0,
    // Hide scrollbar but keep scroll functionality
    scrollbarWidth: "none", // Firefox
    msOverflowStyle: "none", // IE/Edge
    '&::-webkit-scrollbar': {
      display: 'none', // Chrome/Safari/Opera
    },
  },

  chainOfThoughtContainer: {
    alignSelf: "flex-start",
    width: "323px", // Fixed width to match copilot message bubbles output state
    maxWidth: "323px",
    minWidth: "323px",
    minHeight: "140px", // Even more height for morse code dots
    overflow: "visible", // Allow content to extend beyond container if needed
    
    // Nuclear option - override EVERYTHING
    '& *': {
      width: '100% !important',
      maxWidth: '100% !important',
      minWidth: '0 !important',
    },
    
    // Override the root ChainOfThought component
    '& > div': {
      width: '100% !important',
      maxWidth: '100% !important',
      minWidth: '100% !important',
      display: 'block !important',
      minHeight: '140px !important', // Even more content area height
      overflow: 'visible !important', // Ensure content isn't clipped
    },
    
    // Force the entire progress section to be inline
    '& > div:nth-child(2)': {
      display: 'flex !important',
      alignItems: 'center !important',
      gap: '8px !important',
      paddingTop: '20px !important',
      paddingBottom: '20px !important',
      minHeight: '70px !important', // Even more height for morse code
      overflow: 'visible !important',
    },
    
    // Force ALL children in the progress area to be inline
    '& > div:nth-child(2) > *': {
      display: 'inline-block !important',
      verticalAlign: 'middle !important',
    },
    
    // Ensure morse code specifically has room and doesn't get clipped
    '& [data-testid="morse-code"], & svg, & canvas': {
      flex: 'none !important',
      minWidth: 'auto !important',
      overflow: 'visible !important',
    },
    
    // Fix text wrapping issues
    '& h2, & h3, & h4, & h5, & h6': {
      whiteSpace: 'nowrap !important',
      overflow: 'visible !important',
      textOverflow: 'unset !important',
    },
    
    // Fix "Reasoning" header text wrapping
    '& [data-testid="chain-of-thought-header"], & > div:first-child': {
      whiteSpace: 'nowrap !important',
      overflow: 'visible !important',
      fontSize: '14px !important',
      display: 'flex !important',
      alignItems: 'center !important',
      gap: '6px !important',
    },
    
    // More specific selector for reasoning header to ensure 6px gap
    '& > div:first-child > *': {
      margin: '0 !important',
      padding: '0 !important',
    },
    
    '& > div:first-child > *:not(:last-child)': {
      marginRight: '6px !important',
    },
    
    // Ensure chevron icon has no extra spacing
    '& > div:first-child button, & > div:first-child svg, & > div:first-child [role="button"]': {
      margin: '0 !important',
      padding: '0 !important',
      width: 'auto !important',
      height: 'auto !important',
      minWidth: 'auto !important',
      minHeight: 'auto !important',
    },
    
    // Fix progress message text wrapping - be more specific about targeting text only
    '& [data-testid="chain-of-thought-progress-message"]': {
      whiteSpace: 'nowrap !important',
      overflow: 'hidden !important',
      textOverflow: 'ellipsis !important',
      fontSize: '12px !important',
      maxWidth: '160px !important',
      flex: 'none !important',
      display: 'inline-block !important',
    },
    
    // Target any span that contains text (but not morse code)
    '& > div:nth-child(2) span:not([data-testid*="morse"])': {
      whiteSpace: 'nowrap !important',
      overflow: 'hidden !important',
      textOverflow: 'ellipsis !important',
      fontSize: '12px !important',
      maxWidth: '160px !important',
      flex: 'none !important',
      display: 'inline-block !important',
    },
    
    '& [data-testid="chain-of-thought-header"]': {
      fontSize: '14px !important',
    },
    // Target the reasoning header text - more specific selectors
    '& > div:first-child': {
      fontSize: '14px !important',
    },
    '& > div:first-child span': {
      fontSize: '14px !important',
    },
    '& > div:first-child div': {
      fontSize: '14px !important',
    },
    // Target any "Reasoning" text specifically
    '& div:contains("Reasoning")': {
      fontSize: '14px !important',
    },
    // Target the progress message text (next to morse code dots)
    '& > div:first-child + div': {
      fontSize: '12px !important',
    },
    // Target any text elements within the morse code progress area
    '& span:not(:first-child div span)': {
      fontSize: '12px !important',
    },
    // More specific targeting for the morse code progress text
    '& div[role="progressbar"] + span': {
      fontSize: '12px !important',
    },
    // Target the step text that appears next to the dots
    '& div:nth-child(2) span': {
      fontSize: '12px !important',
    },
    // Additional targeting for any nested text in progress area
    '& > div:nth-child(2) *': {
      fontSize: '12px !important',
    },
  },

  // Markdown content styling for Copilot messages
  markdownContent: {
    fontSize: '14px',
    lineHeight: '20px',
    color: copilotTokens.colorNeutralForeground1,
    
    '& p': {
      margin: '0 0 8px 0',
      '&:last-child': {
        marginBottom: 0,
      },
    },
    
    '& strong': {
      fontWeight: 600,
    },
    
    '& em': {
      fontStyle: 'italic',
    },
    
    '& ul, & ol': {
      margin: '8px 0',
      paddingLeft: '20px',
      '&:last-child': {
        marginBottom: 0,
      },
    },
    
    '& li': {
      marginBottom: '4px',
      '&:last-child': {
        marginBottom: 0,
      },
    },
    
    '& h1, & h2, & h3, & h4, & h5, & h6': {
      fontWeight: 600,
      marginTop: '12px',
      marginBottom: '8px',
      '&:first-child': {
        marginTop: 0,
      },
    },
    
    '& h1': { fontSize: '18px' },
    '& h2': { fontSize: '16px' },
    '& h3': { fontSize: '15px' },
    '& h4, & h5, & h6': { fontSize: '14px' },
    
    '& code': {
      backgroundColor: copilotTokens.colorNeutralBackground4,
      padding: '2px 4px',
      borderRadius: '4px',
      fontSize: '13px',
      fontFamily: 'Consolas, Monaco, monospace',
    },
    
    '& pre': {
      backgroundColor: copilotTokens.colorNeutralBackground4,
      padding: '12px',
      borderRadius: '8px',
      overflowX: 'auto',
      margin: '8px 0',
      '& code': {
        backgroundColor: 'transparent',
        padding: 0,
      },
    },
    
    '& blockquote': {
      borderLeft: `3px solid ${copilotTokens.colorBrandBackground}`,
      marginLeft: 0,
      paddingLeft: '12px',
      color: copilotTokens.colorNeutralForeground2,
    },
    
    '& a': {
      color: copilotTokens.colorBrandForeground1,
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
  },
});

const handlePrimaryAction = () => console.log("clicking primary action");

export const CopilotPane: React.FC<CopilotPaneProps> = ({ isOpen, onClose, appType, initialMessage, documentContent }) => {
  const [inputText, setInputText] = useState('');
  const [isChatMode, setIsChatMode] = useState(false);
  const llmContext = useLLMContext();
  const [messages, setMessages] = useState<Array<{ 
    id: string; 
    type: 'user' | 'copilot'; 
    content: string; 
    timestamp: Date;
    isLoading?: boolean;
    isStreaming?: boolean;
    imageData?: string; // Base64 image data
    isImageGeneration?: boolean; // Flag to indicate this is an image generation response
    chainOfThoughtEntries?: Array<{
      header: string;
      content: string;
    }>;
    chainOfThoughtProgress?: number;
  }>>([]);
  const [selectedToggle, setSelectedToggle] = useState<'briefcase' | 'globe'>('briefcase');
  const [forceRender, setForceRender] = useState(0);
  const [initialMessageProcessed, setInitialMessageProcessed] = useState(false);
  const [pendingUserMessage, setPendingUserMessage] = useState<string | null>(null);
  const [dynamicSuggestions, setDynamicSuggestions] = useState<string[]>([
    'Summarize this document',
    'What are the key points?'
  ]);
  const [, setSuggestionsLoading] = useState(false);
  const scrollDiv = React.useRef<HTMLDivElement>(null);
  const brandColors = getBrandColors(appType);
  const styles = useStyles();
  
  useStaticStyles();

  // Force re-render for first message to fix width issue
  useEffect(() => {
    if (messages.length > 0 && messages.some(m => m.isLoading)) {
      const timer = setTimeout(() => {
        setForceRender(prev => prev + 1);
      }, 50); // Small delay to let the component initialize
      return () => clearTimeout(timer);
    }
  }, [messages]);

  // Debug: Monitor messages state changes
  React.useEffect(() => {
    console.log('Messages state changed:', messages);
    console.log('Messages length:', messages.length);
  }, [messages]);

  // Auto-scroll to bottom when new messages are added
  React.useEffect(() => {
    if (scrollDiv.current && isChatMode) {
      scrollDiv.current.scrollTo({ top: scrollDiv.current.scrollHeight, behavior: 'smooth' });
    }
  }, [messages, isChatMode]);

  // Handle initial message when pane opens
  React.useEffect(() => {
    if (isOpen && initialMessage && !initialMessageProcessed) {
      setInitialMessageProcessed(true);
      setIsChatMode(true);
      
      if (initialMessage.type === 'assistant') {
        // Show as AI response (for summary)
        const assistantMessage = {
          type: 'copilot' as const,
          content: initialMessage.content,
          id: `copilot-${Date.now()}`,
          timestamp: new Date(),
        };
        setMessages([assistantMessage]);
      } else {
        // Queue user message to be sent (for pills)
        setPendingUserMessage(initialMessage.content);
      }
    }
  }, [isOpen, initialMessage, initialMessageProcessed]);

  // Reset initialMessageProcessed when pane closes
  React.useEffect(() => {
    if (!isOpen) {
      setInitialMessageProcessed(false);
    }
  }, [isOpen]);

  // Generate dynamic suggestions based on document content
  React.useEffect(() => {
    const generateSuggestions = async () => {
      if (!isOpen || !documentContent || documentContent.length < 50) {
        return;
      }
      
      if (!azureOpenAIService.isConfigured()) {
        return;
      }
      
      setSuggestionsLoading(true);
      
      try {
        const prompt = `Based on the following document content, generate exactly 2 short, helpful questions or actions that a user might want to ask about this document. Each suggestion should be concise (under 50 characters) and actionable.

DOCUMENT CONTENT:
${documentContent.substring(0, 3000)}

---

Return ONLY the 2 suggestions, one per line, without numbering or bullet points. Examples of good suggestions:
- Summarize the main points
- What are the key findings?
- Explain the methodology
- List the action items`;

        const response = await azureOpenAIService.generateResponse(prompt);
        if (response) {
          const suggestions = response
            .split('\n')
            .map(s => s.trim())
            .map(s => s.replace(/^[-•*]\s*/, '')) // Remove leading dashes, bullets, asterisks
            .map(s => s.replace(/^\d+\.\s*/, '')) // Remove leading numbers like "1. "
            .filter(s => s.length > 0 && s.length < 60)
            .slice(0, 2);
          
          if (suggestions.length > 0) {
            setDynamicSuggestions(suggestions);
          }
        }
      } catch (error) {
        console.error('Failed to generate suggestions:', error);
      } finally {
        setSuggestionsLoading(false);
      }
    };
    
    generateSuggestions();
  }, [isOpen, documentContent]);

  if (!isOpen) return null;

  const handleChatSubmit = (message: string) => {
    console.log('=== handleChatSubmit START ===');
    console.log('Message received:', message);
    console.log('Current messages state:', messages);
    console.log('Current isChatMode:', isChatMode);
    
    if (!message || !message.trim()) {
      console.log('Empty message, returning early');
      return;
    }

    // Create user message
    const userMessage = {
      type: 'user' as const,
      content: message.trim(),
      id: `user-${Date.now()}`,
      timestamp: new Date(),
    };

    // Detect if this is an image generation request early
    const isImageRequest = llmContext.llmService?.isImageGenerationRequest(message.trim()) || false;
    
    // Create initial loading message with appropriate chain of thought
    const imageChainOfThought = [
      { header: "Analyzing request", content: "Understanding your image generation request" },
      { header: "Preparing generation", content: "Setting up image generation parameters" },
      { header: "Creating image", content: "Generating image with AI models" },
      { header: "Finalizing", content: "Processing and optimizing the result" },
    ];

    const textChainOfThought = [
      { header: "Understanding question", content: "Analyzing your request and context" },
      { header: "Researching", content: "Gathering relevant information" },
      { header: "Generating response", content: "Formulating a comprehensive answer" },
      { header: "Finalizing", content: "Reviewing and polishing the response" },
    ];

    const loadingMessage = {
      type: 'copilot' as const,
      content: isImageRequest ? 'Generating your image...' : 'Thinking...',
      id: `copilot-${Date.now() + 1}`,
      timestamp: new Date(),
      isLoading: true,
      chainOfThoughtEntries: isImageRequest ? imageChainOfThought : textChainOfThought,
      chainOfThoughtProgress: 0,
    };

    console.log('Created userMessage:', userMessage);
    console.log('Created loadingMessage:', loadingMessage);

    // Add user message and loading message
    setMessages(prevMessages => {
      const newMessages = [...prevMessages, userMessage, loadingMessage];
      console.log('Added loading messages:', newMessages);
      return newMessages;
    });

    // Make sure we're in chat mode
    if (!isChatMode) {
      console.log('Setting chat mode to true');
      setIsChatMode(true);
    }

    // Simulate chain of thought progression with faster timing for text
    let currentProgress = 0;
    
    // Faster progression for text, slower for images
    const progressDelay = isImageRequest ? 2000 : 800; // 0.8s for text, 2s for images
    const finalDelay = isImageRequest ? 1500 : 500; // 0.5s for text, 1.5s for images
    
    const progressInterval = setInterval(() => {
      if (currentProgress < (isImageRequest ? imageChainOfThought.length - 1 : textChainOfThought.length - 1)) {
        currentProgress++;
        setMessages(prevMessages => {
          return prevMessages.map(msg => {
            if (msg.id === loadingMessage.id && msg.isLoading) {
              return {
                ...msg,
                chainOfThoughtProgress: currentProgress,
              };
            }
            return msg;
          });
        });
      } else {
        clearInterval(progressInterval);
        
        // After completing chain of thought, show final response with reduced delay
        setTimeout(async () => {
          // Generate contextual fallback response for when LLM is not available
          let fallbackResponse;
          const query = message.trim().toLowerCase();
          
          if (query.includes('joke')) {
            fallbackResponse = "Here's a joke for you: Why don't scientists trust atoms? Because they make up everything! 😄";
          } else if (query.includes('help') || query.includes('assist')) {
            fallbackResponse = `I'm here to help you with your ${appType} document! I can assist with writing, editing, formatting, and more.`;
          } else if (query.includes('email') && query.includes('chris')) {
            fallbackResponse = "I can help you draft an email to Chris. What would you like to include in the email?";
          } else if (query.includes('new') && query.includes('document')) {
            fallbackResponse = `This document appears to be a new ${appType} document. I can help you get started with content, formatting, or structure.`;
          } else {
            fallbackResponse = `I can help you with that! You asked about: "${message.trim()}". I'm ready to assist with your ${appType} document.`;
          }
          
          // Generate response using LLM if available, otherwise use fallback
          let finalContent = fallbackResponse;
          let imageData: string | null = null;
          let isImageGeneration = false;
          
          console.log('LLM Context Debug:', {
            isLLMEnabled: llmContext?.isLLMEnabled,
            isLLMAuthenticated: llmContext?.isLLMAuthenticated,
            hasLLMService: !!llmContext?.llmService,
            llmContextExists: !!llmContext,
            serviceType: llmContext?.llmService?.constructor?.name,
            azureOpenAIConfigured: azureOpenAIService.isConfigured()
          });
          
          // Priority 1: Try Azure OpenAI if configured
          if (azureOpenAIService.isConfigured()) {
            try {
              console.log('Using Azure OpenAI for response generation');
              console.log('Document content available:', !!documentContent, 'length:', documentContent?.length || 0);
              
              // Build context-aware prompt
              let prompt: string;
              if (documentContent && documentContent.length > 50) {
                // Include document content for context-aware responses (limit to first 10000 chars)
                const docContext = documentContent.substring(0, 10000);
                prompt = `You are a helpful AI assistant in Microsoft ${appType}. You have access to the user's current document content.

DOCUMENT CONTENT:
${docContext}

---

Based on the document above, please answer the user's question. If the question is about the document, use the document content to provide a relevant and accurate answer. If the question is not related to the document, provide a helpful general response.

USER'S QUESTION: ${message.trim()}

Please provide a helpful, concise response:`;
              } else {
                prompt = `You are a helpful AI assistant in Microsoft ${appType}. Please provide a helpful, concise response to the user's message. Keep your response focused and practical.\n\nUser message: ${message.trim()}`;
              }
              
              const response = await azureOpenAIService.generateResponse(prompt);
              if (response) {
                finalContent = response;
                console.log('Azure OpenAI response:', finalContent);
              }
            } catch (azureError) {
              console.error('Azure OpenAI error:', azureError);
              // Fall through to other options
            }
          }
          // Priority 2: Try Microsoft LLM service if available
          else if (llmContext?.isLLMEnabled && llmContext?.isLLMAuthenticated && llmContext.llmService) {
            try {
              console.log('Is image generation request:', isImageRequest, 'for message:', message.trim());
              
              if (isImageRequest) {
                isImageGeneration = true;
                const imagePrompt = llmContext.llmService.extractImagePrompt(message.trim());
                console.log('Image generation request detected. Prompt:', imagePrompt);
                
                // Generate the image with a timeout
                try {
                  const timeoutPromise = new Promise<string | null>((_, reject) => {
                    setTimeout(() => reject(new Error('Image generation timeout')), 25000); // 25 second timeout
                  });
                  
                  const imagePromise = llmContext.llmService.generateImage(imagePrompt);
                  imageData = await Promise.race([imagePromise, timeoutPromise]);
                  
                  console.log('Image generation result:', imageData ? 'Success' : 'Failed');
                  
                  if (imageData) {
                    finalContent = `I've generated an image for you based on: "${imagePrompt}"`;
                  } else {
                    finalContent = "I wasn't able to generate an image at the moment. The image generation service might be unavailable. Please try again later.";
                  }
                } catch (imageError) {
                  console.error('Image generation error:', imageError);
                  finalContent = "Image generation failed due to a timeout or service error. Please try again with a simpler prompt.";
                }
              } else {
                // Regular text generation - let LLM generate the actual response
                try {
                  // Call LLM to generate a proper response (without streaming for now)
                  console.log('Calling generateResponse for:', message.trim());
                  finalContent = await llmContext.llmService.generateResponse(message.trim()) || fallbackResponse;
                  console.log('LLM generated response:', finalContent);
                } catch (llmError) {
                  console.error('LLM generation failed:', llmError);
                  finalContent = fallbackResponse; // Use fallback if LLM fails
                }
              }
            } catch (error) {
              console.error('Error with LLM service:', error);
              finalContent = fallbackResponse; // Fallback to base response
            }
          } else if (!azureOpenAIService.isConfigured()) {
            console.log('No LLM service available - Azure OpenAI not configured and Microsoft LLM not authenticated');
          }

          const finalResponse = {
            type: 'copilot' as const,
            content: finalContent,
            id: loadingMessage.id,
            timestamp: new Date(),
            isLoading: false,
            imageData: imageData || undefined,
            isImageGeneration: isImageGeneration,
          };

          setMessages(prevMessages => {
            return prevMessages.map(msg => {
              if (msg.id === loadingMessage.id) {
                return finalResponse;
              }
              return msg;
            });
          });
        }, finalDelay);
      }
    }, progressDelay); // Use dynamic timing

    console.log('=== handleChatSubmit END ===');
  };

  // Handle pending user message (from pill clicks) - auto-send after handleChatSubmit is defined
  React.useEffect(() => {
    if (pendingUserMessage) {
      // Small delay to ensure the pane is fully rendered
      const timer = setTimeout(() => {
        handleChatSubmit(pendingUserMessage);
        setPendingUserMessage(null);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [pendingUserMessage]);

  const handlePromptStarterClick = (prompt: string) => {
    setInputText(prompt);
    // Auto-send the prompt starter message
    setTimeout(() => {
      const cleanPrompt = prompt.replace(/<[^>]*>/g, ''); // Remove HTML tags
      handleChatSubmit(cleanPrompt);
      setInputText('');
    }, 100);
  };

  const data = [
    {
      icon: (
        <Image
          height="20px"
          width="20px"
          alt="Word"
          src="https://res-1.cdn.office.net/files/fabric-cdn-prod_20230815.002/assets/brand-icons/product/svg/word_48x1.svg"
        />
      ),
      prompt: (
        <>
          Identify the primary benefits in{" "}
          <span style={{ color: brandColors.foreground, fontWeight: copilotTokens.fontWeightSemibold }}>End of Quarter Numbers</span>
        </>
      ),
      reasonMarker: "You updated this",
    },
    {
      icon: (
        <Avatar
          name="Mona Kane"
          image={{
            src: "https://fabricweb.azureedge.net/fabric-website/assets/images/avatar/MonaKane.jpg",
          }}
          size={20}
        />
      ),
      prompt: "What's new from Mona Kane?",
      reasonMarker: "Is a close collaborator",
    },
    {
      icon: <Calendar20Regular />,
      prompt: (
        <>
          List actions items for me from{" "}
          <span style={{ color: brandColors.foreground, fontWeight: copilotTokens.fontWeightSemibold }}>BCX Feature review</span>
        </>
      ),
    },
    {
      icon: (
        <Avatar
          name="Daisy Phillips"
          image={{
            src: "https://fabricweb.azureedge.net/fabric-website/assets/images/avatar/DaisyPhillips.jpg",
          }}
          size={20}
        />
      ),
      prompt: "When's my next meeting with Daisy Phillips",
      reasonMarker: "Had a recent 1:1 conversation",
    },
    {
      icon: (
        <Image
          height="20px"
          width="20px"
          alt="Word"
          src="https://res-1.cdn.office.net/files/fabric-cdn-prod_20230815.002/assets/brand-icons/product/svg/word_48x1.svg"
        />
      ),
      prompt: (
        <>
          Summarize <span style={{ color: brandColors.foreground, fontWeight: copilotTokens.fontWeightSemibold }}>The Era of AI</span>
        </>
      ),
      reasonMarker: "You updated this",
    },
    {
      icon: <Chat20Regular />,
      prompt: "What should be on my radar from emails last week?",
      reasonMarker: "Suggested for you",
    },
    {
      icon: <Chat20Regular />,
      prompt: (
        <>
          What is my latest from{" "}
          <span style={{ color: brandColors.foreground, fontWeight: copilotTokens.fontWeightSemibold }}>person</span> organized by
          emails and messages?
        </>
      ),
    },
    {
      icon: <Calendar20Regular />,
      prompt: (
        <>
          Recap <span style={{ color: brandColors.foreground, fontWeight: copilotTokens.fontWeightSemibold }}>Weekly Sync</span>
        </>
      ),
      reasonMarker: "You may be interested in this",
    },
    {
      icon: (
        <Image
          height="20px"
          width="20px"
          alt="Word"
          src="https://res-1.cdn.office.net/files/fabric-cdn-prod_20230815.002/assets/brand-icons/product/svg/word_48x1.svg"
        />
      ),
      prompt: (
        <>
          List key points from{" "}
          <span style={{ color: brandColors.foreground, fontWeight: copilotTokens.fontWeightSemibold }}>
            Team Processes FY26 Planning
          </span>
        </>
      ),
      reasonMarker: "You updated this",
    },
  ];

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <Button
            className={styles.brandHoverButton}
            appearance="transparent"
            icon={<TextAlignLeft />}
            size="small"
            style={{ '--brand-color': brandColors.primary } as React.CSSProperties}
          />
          <Button
            className={styles.greenIconButton}
            appearance="transparent"
            icon={<ShieldTask />}
            size="small"
          />
        </div>
        
        <div className={styles.headerCenter}>
          <ToggleButton
            className={`${selectedToggle === 'briefcase' ? styles.selectedToggleButton : styles.brandHoverButton} ${styles.toggleButtonLeft}`}
            appearance="secondary"
            icon={selectedToggle === 'briefcase' ? <Briefcase20Filled /> : <Briefcase20Regular />}
            size="small"
            checked={selectedToggle === 'briefcase'}
            onClick={() => setSelectedToggle('briefcase')}
            style={{
              color: selectedToggle === 'briefcase' ? brandColors.primary : undefined,
              backgroundColor: selectedToggle === 'briefcase' ? copilotTokens.colorNeutralBackground1 : 'transparent',
              borderTopLeftRadius: copilotTokens.borderRadiusXLarge,
              borderBottomLeftRadius: copilotTokens.borderRadiusXLarge,
              borderTopRightRadius: '0',
              borderBottomRightRadius: '0',
              borderRight: 'none',
            } as React.CSSProperties}
          />
          <ToggleButton
            className={`${selectedToggle === 'globe' ? styles.selectedToggleButton : styles.brandHoverButton} ${styles.toggleButtonRight}`}
            appearance="secondary"
            icon={selectedToggle === 'globe' ? <Globe20Filled /> : <Globe20Regular />}
            size="small"
            checked={selectedToggle === 'globe'}
            onClick={() => setSelectedToggle('globe')}
            style={{
              color: selectedToggle === 'globe' ? brandColors.primary : undefined,
              backgroundColor: selectedToggle === 'globe' ? copilotTokens.colorNeutralBackground1 : 'transparent',
              borderTopRightRadius: copilotTokens.borderRadiusXLarge,
              borderBottomRightRadius: copilotTokens.borderRadiusXLarge,
              borderTopLeftRadius: '0',
              borderBottomLeftRadius: '0',
            } as React.CSSProperties}
          />
        </div>

        <div className={styles.headerRight}>
          <Button
            className={styles.iconButton}
            appearance="primary"
            icon={<Compose20 />}
            size="small"
            style={{
              backgroundColor: brandColors.primary,
              borderColor: brandColors.primary,
              color: copilotTokens.colorNeutralForegroundOnBrand,
            }}
          />
          <Button
            className={styles.brandHoverButton}
            appearance="transparent"
            icon={<MoreHorizontal20Regular />}
            size="small"
            style={{ '--brand-color': brandColors.primary } as React.CSSProperties}
          />
          <Button
            className={styles.brandHoverButton}
            appearance="transparent"
            icon={<Dismiss20 />}
            size="small"
            onClick={onClose}
            style={{ '--brand-color': brandColors.primary } as React.CSSProperties}
          />
        </div>
      </div>

      {/* Fluent UI Copilot Chat Content */}
      <div className={styles.chatContainer}>
        {isChatMode ? (
          /* Chat View */
          <div className={styles.chatView}>
            <CopilotProvider mode="sidecar" {...CopilotTheme} style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
              <CopilotChat className={styles.chat} ref={scrollDiv}>
                {messages.map((message) => {
                  if (message.type === 'user') {
                    return (
                      <UserMessage key={message.id}>
                        {message.content}
                      </UserMessage>
                    );
                  } else {
                    return (
                      <CopilotMessage
                        key={message.id}
                        name="Copilot"
                        avatar={
                          <Image
                            src="https://res-2-sdf.cdn.office.net/files/fabric-cdn-prod_20240411.001/assets/brand-icons/product/svg/copilot_24x1.svg"
                            alt="Copilot"
                          />
                        }
                        loadingState="none"
                        actions={!message.isLoading ? (
                          <div 
                            className={styles.feedbackButtons}
                            style={{ '--brand-color': brandColors.primary } as React.CSSProperties}
                          >
                            <FeedbackButtons />
                          </div>
                        ) : undefined}
                      >
                        {(() => {
                          console.log('Rendering message:', {
                            isLoading: message.isLoading,
                            hasChainOfThought: !!message.chainOfThoughtEntries,
                            entriesLength: message.chainOfThoughtEntries?.length,
                            progress: message.chainOfThoughtProgress,
                            currentEntry: message.chainOfThoughtEntries?.[message.chainOfThoughtProgress || 0]
                          });
                          
                          if (message.isLoading && message.chainOfThoughtEntries && message.chainOfThoughtEntries.length > 0) {
                            return (
                              <div className={styles.chainOfThoughtContainer}>
                                <ChainOfThought
                                  key={`${message.id}-${message.chainOfThoughtProgress}-${forceRender}`}
                                  currentStep={message.chainOfThoughtEntries[message.chainOfThoughtProgress || 0]?.header}
                                  headerText="Reasoning"
                                  progressMessage={message.chainOfThoughtEntries[message.chainOfThoughtProgress || 0]?.header || "Beginning research"}
                                  progressState="loading"
                                  expanded={false}
                                  style={{ 
                                    width: "323px !important",
                                    maxWidth: "323px !important",
                                    minWidth: "323px !important",
                                    minHeight: "200px",
                                    '--reasoning-header-font-size': '16px',
                                    '--progress-message-font-size': '14px',
                                  } as React.CSSProperties}
                                >
                                  {message.chainOfThoughtEntries.slice(0, (message.chainOfThoughtProgress || 0) + 1).map((item, index) => (
                                    <ChainOfThoughtItem
                                      key={`${message.id}-${index}`}
                                      headerText={item.header}
                                      active={index === (message.chainOfThoughtProgress || 0)}
                                    >
                                      {item.content}
                                    </ChainOfThoughtItem>
                                  ))}
                                </ChainOfThought>
                              </div>
                            );
                          } else if (message.isLoading) {
                            return <MorseCode />;
                          } else {
                            return (
                              <div className={styles.markdownContent}>
                                <ReactMarkdown>{message.content}</ReactMarkdown>
                                {message.imageData && (
                                  <div style={{ marginTop: '12px' }}>
                                    <img 
                                      src={`data:image/png;base64,${message.imageData}`}
                                      alt="Generated image"
                                      style={{
                                        maxWidth: '100%',
                                        height: 'auto',
                                        borderRadius: '8px',
                                        border: '1px solid #e1dfdd'
                                      }}
                                    />
                                  </div>
                                )}
                              </div>
                            );
                          }
                        })()}
                      </CopilotMessage>
                    );
                  }
                })}
              </CopilotChat>

              {/* Chat Input - Reuse the same input box from default mode */}
              <div className={styles.chatInputContainer}>
                {/* Only show suggestions when AI is not loading */}
                {!messages.some(m => m.isLoading) && (
                  <SuggestionList>
                    {dynamicSuggestions.map((suggestion, index) => (
                      <Suggestion 
                        key={index}
                        onClick={() => handlePromptStarterClick(suggestion)}
                      >
                        {suggestion}
                      </Suggestion>
                    ))}
                  </SuggestionList>
                )}
                
                <div style={{ 
                  border: '1px solid #e1dfdd', 
                  borderRadius: '24px',
                  paddingTop: '16px',
                  paddingBottom: '8px',
                  paddingLeft: '0px',
                  paddingRight: '0px',
                  display: 'flex', 
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  backgroundColor: '#fff',
                  height: '104px',
                  width: '100%',
                  boxSizing: 'border-box',
                  transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
                  cursor: 'text'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0px 2px 2px rgba(0, 0, 0, 0.1), 0px 0px 2px rgba(0, 0, 0, 0.06)';
                }}
                onMouseLeave={(e) => {
                  if (!e.currentTarget.contains(document.activeElement)) {
                    e.currentTarget.style.boxShadow = 'none';
                  }
                }}
                onFocusCapture={(e) => {
                  e.currentTarget.style.boxShadow = '0px 2px 4px rgba(0, 0, 0, 0.1), 0px 0px 2px rgba(0, 0, 0, 0.06)';
                }}
                onBlurCapture={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                    e.currentTarget.style.boxShadow = 'none';
                  }
                }}
              >
                {/* Text input area - takes up most space */}
                <textarea 
                  style={{ 
                    border: 'none', 
                    outline: 'none', 
                    width: '100%', 
                    fontSize: '14px',
                    backgroundColor: 'transparent',
                    resize: 'none',
                    flex: 1,
                    lineHeight: '20px',
                    padding: '0 16px',
                    fontFamily: 'Segoe UI, system-ui, sans-serif',
                    overflow: 'hidden',
                    color: '#323130',
                    boxSizing: 'border-box'
                  }}
                  placeholder="Ask questions and work with this document"
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      if (inputText.trim()) {
                        handleChatSubmit(inputText);
                        setInputText('');
                      }
                    }
                  }}
                  onFocus={(e) => {
                    // Focus styling is handled by the parent container
                    e.target.style.color = '#323130';
                  }}
                />
                
                {/* All action buttons in one row at bottom */}
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  paddingTop: '8px',
                  paddingLeft: '12px',
                  paddingRight: '12px',
                }}>
                  <Button
                    aria-label="Add"
                    shape="circular"
                    icon={<Add20 />}
                    appearance="transparent"
                    size="small"
                    className={styles.brandHoverButton}
                    style={{ '--brand-color': brandColors.primary } as React.CSSProperties}
                  />
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <Button
                      aria-label="record message"
                      appearance="transparent"
                      icon={<Mic20 />}
                      size="small"
                      shape="circular"
                      className={styles.brandHoverButton}
                      style={{ '--brand-color': brandColors.primary } as React.CSSProperties}
                    />
                    <Button
                      aria-label={inputText.trim() ? "Send message" : "Device EQ"}
                      appearance={inputText.trim() ? "primary" : "transparent"}
                      icon={inputText.trim() ? <ArrowRight16 /> : <DeviceEq20 />}
                      size="small"
                      shape="circular"
                      className={inputText.trim() ? styles.iconButton : styles.brandHoverButton}
                      onClick={inputText.trim() ? () => {
                        handleChatSubmit(inputText);
                        setInputText('');
                      } : undefined}
                      style={inputText.trim() ? {
                        backgroundColor: brandColors.primary,
                        borderColor: brandColors.primary,
                      } : { '--brand-color': brandColors.primary } as React.CSSProperties}
                    />
                  </div>
                </div>
              </div>
              </div>
            </CopilotProvider>
          </div>
        ) : (
          <>
            {/* Top spacer for centering */}
            <div style={{ flex: '1 1 0', minHeight: 0 }}></div>
            
            <div className={styles.provider}>
              <div className={styles.headerSection} style={{ textAlign: 'center' }}>
                <Title1 style={{ fontSize: '24px' }}>Hi, how can I help you with?</Title1>
              </div>
          
          <div style={{ 
            paddingTop: '16px',
            width: '100%',
            boxSizing: 'border-box'
          }}>
            <div 
              style={{ 
                border: '1px solid #e1dfdd', 
                borderRadius: '20px',
                padding: '16px', 
                display: 'flex', 
                flexDirection: 'column',
                justifyContent: 'space-between',
                backgroundColor: '#fff',
                height: '104px',
                width: '100%',
                boxSizing: 'border-box',
                transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
                cursor: 'text'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0px 2px 4px rgba(0, 0, 0, 0.1), 0px 0px 2px rgba(0, 0, 0, 0.06)';
              }}
              onMouseLeave={(e) => {
                if (!e.currentTarget.contains(document.activeElement)) {
                  e.currentTarget.style.boxShadow = 'none';
                }
              }}
              onFocusCapture={(e) => {
                e.currentTarget.style.boxShadow = '0px 2px 4px rgba(0, 0, 0, 0.1), 0px 0px 2px rgba(0, 0, 0, 0.06)';
              }}
              onBlurCapture={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                  e.currentTarget.style.boxShadow = 'none';
                }
              }}
            >
              {/* Text input area - takes up most space */}
              <textarea 
                style={{ 
                  border: 'none', 
                  outline: 'none', 
                  width: '100%', 
                  fontSize: '14px',
                  backgroundColor: 'transparent',
                  resize: 'none',
                  flex: 1,
                  lineHeight: '20px',
                  padding: '0',
                  fontFamily: 'Segoe UI, system-ui, sans-serif',
                  overflow: 'hidden',
                  color: '#323130'
                }}
                placeholder="Ask questions and work with this document"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (inputText.trim()) {
                      handleChatSubmit(inputText);
                      setInputText('');
                    }
                  }
                }}
                onFocus={(e) => {
                  // Focus styling is handled by the parent container
                  e.target.style.color = '#323130';
                }}
              />
              
              {/* All action buttons in one row at bottom */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                paddingTop: '8px'
              }}>
                <Button
                  aria-label="Add"
                  shape="circular"
                  icon={<Add20 />}
                  appearance="transparent"
                  size="small"
                  className={styles.brandHoverButton}
                  style={{ '--brand-color': brandColors.primary } as React.CSSProperties}
                />
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <Button
                    aria-label="record message"
                    appearance="transparent"
                    icon={<Mic20 />}
                    size="small"
                    shape="circular"
                    className={styles.brandHoverButton}
                    style={{ '--brand-color': brandColors.primary } as React.CSSProperties}
                  />
                  <Button
                    aria-label={inputText.trim() ? "Send message" : "Device EQ"}
                    appearance={inputText.trim() ? "primary" : "transparent"}
                    icon={inputText.trim() ? <ArrowRight16 /> : <DeviceEq20 />}
                    size="small"
                    shape="circular"
                    className={inputText.trim() ? styles.iconButton : styles.brandHoverButton}
                    onClick={inputText.trim() ? () => {
                      handleChatSubmit(inputText);
                      setInputText('');
                    } : undefined}
                    style={inputText.trim() ? {
                      backgroundColor: brandColors.primary,
                      borderColor: brandColors.primary,
                    } : { '--brand-color': brandColors.primary } as React.CSSProperties}
                  />
                </div>
              </div>
            </div>
          </div>
          
                    <CopilotProvider mode="sidecar" {...CopilotTheme}>
            <div className={styles.promptStarterContainer}>
              <PromptStarterList className={styles.promptStarterList}>
                {data.map((promptStarterData, index) => (
                  <PromptStarter
                    key={index}
                    primaryAction={{ onClick: handlePrimaryAction }}
                    actions={<PromptStarterActions />}
                    {...promptStarterData}
                  />
                ))}
              </PromptStarterList>
            </div>
          </CopilotProvider>
        </div>
        
        {/* Bottom spacer for centering */}
        <div style={{ flex: '1 1 0', minHeight: 0 }}></div>
        
        {/* Premium Badge - anchored to bottom */}
        <div className={styles.premiumBadge}>
          <img src={premiumStarIcon} alt="" className={styles.premiumIcon} />
          <span className={styles.premiumText}>You have premium Copilot features enabled</span>
        </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CopilotPane;
